import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useUserContext } from "@/context/user-context";
import { AlertTriangle, LogOut } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

interface LogoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Logout Dialog
export function LogoutDialog({ open, onOpenChange }: LogoutDialogProps) {
  const { logoutAndRedirect } = useAuth();
  const { user } = useUserContext();
  const [error, setError] = useState<string>("");

  const handleLogout = async () => {
    setError("");
    try {
      await logoutAndRedirect();
      onOpenChange(false);
    } catch (error) {
      console.error("Logout failed:", error);
      setError("Failed to sign out. Please try again.");
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setError("");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5" />
            Sign Out
          </SheetTitle>
          <SheetDescription>
            Are you sure you want to sign out? You will be signed out of your
            account and redirected to the login page.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {user && (
            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
              <p>
                <strong>Current user:</strong> {user.firstName} {user.lastName}
              </p>
              <p>Email: {user.email || "Not available"}</p>
              <p>Role: {user.userType || "Not available"}</p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <SheetFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleLogout}
            className="w-full sm:w-auto"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
